/**
 * Panel de Revisión Manual — Fase 1C
 * Módulo TÉCNICO exclusivo del Propietario Global de CELEBRA.
 *
 * Autorización expresa: 22-Jul-2026.
 * Regla dura: NUNCA reasigna automáticamente. Solo el humano decide.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/creador/revision`;

// ── Utilidades ───────────────────────────────────────────────────────────
const esPropietarioGlobal = (user) => {
  if (!user) return false;
  if (user.role_id === "role_propietario_global") return true;
  if (user.role === "superadmin" && user.es_celebra_group === true) return true;
  return false;
};

const Badge = ({ children, variant = "default" }) => {
  const variants = {
    default: "bg-slate-200 text-slate-800",
    warning: "bg-amber-100 text-amber-800",
    danger: "bg-rose-100 text-rose-800",
    success: "bg-emerald-100 text-emerald-800",
    info: "bg-sky-100 text-sky-800",
    pending: "bg-orange-100 text-orange-800",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
};

// ── Panel principal ──────────────────────────────────────────────────────
export default function PanelRevisionManual() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("resumen");
  const [resumen, setResumen] = useState(null);
  const [organizaciones, setOrganizaciones] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [roles, setRoles] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ambiguas, setAmbiguas] = useState({ duplicados_por_telefono: [], duplicados_por_correo: [] });
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(false);

  const cargarResumen = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/resumen`);
      setResumen(data);
    } catch (e) {
      toast.error("Error cargando resumen: " + (e.response?.data?.detail || e.message));
    }
  }, []);

  const cargarUsuarios = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axios.get(`${API}/usuarios?tipo=todos`);
      setUsuarios(data.usuarios || []);
    } catch (e) { toast.error("Error usuarios: " + (e.response?.data?.detail || e.message)); }
    finally { setCargando(false); }
  }, []);

  const cargarEventos = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axios.get(`${API}/eventos?tipo=todos`);
      setEventos(data.eventos || []);
    } catch (e) { toast.error("Error eventos: " + (e.response?.data?.detail || e.message)); }
    finally { setCargando(false); }
  }, []);

  const cargarClientes = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axios.get(`${API}/clientes`);
      setClientes(data.clientes || []);
    } catch (e) { toast.error("Error clientes: " + (e.response?.data?.detail || e.message)); }
    finally { setCargando(false); }
  }, []);

  const cargarOrganizaciones = useCallback(async () => {
    setCargando(true);
    try {
      const [o, u, r] = await Promise.all([
        axios.get(`${API}/organizaciones`),
        axios.get(`${API}/unidades`),
        axios.get(`${API}/roles`),
      ]);
      setOrganizaciones(o.data.organizaciones || []);
      setUnidades(u.data.unidades || []);
      setRoles(r.data.roles || []);
    } catch (e) { toast.error("Error jerarquía: " + (e.response?.data?.detail || e.message)); }
    finally { setCargando(false); }
  }, []);

  const cargarAmbiguas = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axios.get(`${API}/relaciones-ambiguas`);
      setAmbiguas(data);
    } catch (e) { toast.error("Error ambiguas: " + (e.response?.data?.detail || e.message)); }
    finally { setCargando(false); }
  }, []);

  const cargarHistorial = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/historial?limite=50`);
      setHistorial(data.historial || []);
    } catch (e) { toast.error("Error historial: " + (e.response?.data?.detail || e.message)); }
  }, []);

  useEffect(() => {
    if (!loading && esPropietarioGlobal(user)) cargarResumen();
  }, [loading, user, cargarResumen]);

  useEffect(() => {
    if (!esPropietarioGlobal(user)) return;
    if (tab === "usuarios") cargarUsuarios();
    else if (tab === "eventos") cargarEventos();
    else if (tab === "clientes") cargarClientes();
    else if (tab === "jerarquia") cargarOrganizaciones();
    else if (tab === "ambiguas") cargarAmbiguas();
    else if (tab === "historial") cargarHistorial();
  }, [tab, user, cargarUsuarios, cargarEventos, cargarClientes, cargarOrganizaciones, cargarAmbiguas, cargarHistorial]);

  // ── Guard de acceso ─────────────────────────────────────────────────
  if (loading) return <div className="p-8 text-center text-slate-500">Cargando…</div>;
  if (!esPropietarioGlobal(user)) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center" data-testid="guard-403">
        <h1 className="text-2xl font-bold text-rose-700 mb-4">Acceso restringido</h1>
        <p className="text-slate-600">
          El Panel de Revisión Manual es exclusivo del <strong>Propietario Global de CELEBRA</strong>.
        </p>
      </div>
    );
  }

  const decidir = async ({ entidad, entidad_id, campo, accion, valor_sugerido, unit_ids_sugeridos, motivo }) => {
    try {
      const { data } = await axios.post(`${API}/decidir`, {
        entidad, entidad_id, campo, accion, valor_sugerido, unit_ids_sugeridos, motivo,
      });
      toast.success(`Decisión aplicada: ${data.estado}`);
      // Refrescar la tab actual
      if (tab === "usuarios") cargarUsuarios();
      else if (tab === "eventos") cargarEventos();
      else if (tab === "clientes") cargarClientes();
      cargarResumen();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="panel-revision-manual">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-1">Panel de Revisión Manual</h1>
        <p className="text-sm text-slate-500">
          Módulo técnico exclusivo del Propietario Global. Nunca reasigna automáticamente.
        </p>
      </header>

      {/* Tabs */}
      <nav className="flex flex-wrap gap-2 border-b border-slate-200 mb-6" data-testid="tabs">
        {[
          { id: "resumen", label: "Resumen" },
          { id: "jerarquia", label: "Jerarquía" },
          { id: "usuarios", label: `Usuarios (${resumen?.usuarios_sin_role || 0}+${resumen?.usuarios_sin_org || 0})` },
          { id: "eventos", label: `Eventos (${resumen?.eventos_sin_unit_ids || 0})` },
          { id: "clientes", label: `Clientes (${resumen?.clientes_sin_org || 0})` },
          { id: "ambiguas", label: "Ambiguas" },
          { id: "historial", label: "Historial" },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.id
                ? "border-emerald-600 text-emerald-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {cargando && <div className="text-slate-500 mb-3">Cargando…</div>}

      {/* Contenido */}
      {tab === "resumen" && <TabResumen resumen={resumen} />}
      {tab === "jerarquia" && <TabJerarquia organizaciones={organizaciones} unidades={unidades} roles={roles} />}
      {tab === "usuarios" && <TabUsuarios usuarios={usuarios} onDecidir={decidir} />}
      {tab === "eventos" && <TabEventos eventos={eventos} onDecidir={decidir} />}
      {tab === "clientes" && <TabClientes clientes={clientes} onDecidir={decidir} organizaciones={organizaciones} />}
      {tab === "ambiguas" && <TabAmbiguas ambiguas={ambiguas} />}
      {tab === "historial" && <TabHistorial historial={historial} />}
    </div>
  );
}

// ── SUBCOMPONENTES ────────────────────────────────────────────────────
function TabResumen({ resumen }) {
  if (!resumen) return <div className="text-slate-500">Sin datos.</div>;
  const items = [
    { label: "Organizaciones SaaS activas", value: resumen.organizaciones_activas, testid: "kpi-orgs-activas" },
    { label: "Organizaciones SaaS totales", value: resumen.organizaciones_saas_totales, testid: "kpi-orgs-total" },
    { label: "Empresas archivadas", value: resumen.empresas_archivadas, testid: "kpi-empresas-archivadas" },
    { label: "Usuarios sin organización", value: resumen.usuarios_sin_org, testid: "kpi-users-sin-org", severity: resumen.usuarios_sin_org > 0 ? "warning" : "success" },
    { label: "Usuarios sin rol", value: resumen.usuarios_sin_role, testid: "kpi-users-sin-role", severity: resumen.usuarios_sin_role > 0 ? "warning" : "success" },
    { label: "Eventos sin organización", value: resumen.eventos_sin_org, testid: "kpi-eventos-sin-org", severity: resumen.eventos_sin_org > 0 ? "warning" : "success" },
    { label: "Eventos sin unidades", value: resumen.eventos_sin_unit_ids, testid: "kpi-eventos-sin-unit", severity: resumen.eventos_sin_unit_ids > 0 ? "warning" : "success" },
    { label: "Clientes sin organización", value: resumen.clientes_sin_org, testid: "kpi-clientes-sin-org", severity: resumen.clientes_sin_org > 0 ? "warning" : "success" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map(i => (
        <div key={i.label} data-testid={i.testid} className={`p-4 rounded-lg border ${i.severity === "warning" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{i.label}</div>
          <div className="text-3xl font-bold text-slate-900">{i.value}</div>
        </div>
      ))}
      <p className="col-span-full text-xs text-slate-400 mt-2">
        Generado: {resumen.generado_en}
      </p>
    </div>
  );
}

function TabJerarquia({ organizaciones, unidades, roles }) {
  return (
    <div className="space-y-6" data-testid="tab-content-jerarquia">
      <section>
        <h2 className="text-lg font-semibold mb-2">Organizaciones ({organizaciones.length})</h2>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-3 py-2">ID</th><th className="text-left px-3 py-2">Nombre</th>
            <th className="text-left px-3 py-2">Tipo</th><th className="text-left px-3 py-2">Estado</th>
            <th className="text-right px-3 py-2">Users</th><th className="text-right px-3 py-2">Eventos</th>
            <th className="text-right px-3 py-2">Clientes</th><th className="text-right px-3 py-2">Pagos</th>
          </tr></thead>
          <tbody>
            {organizaciones.map(o => (
              <tr key={o.organization_id} data-testid={`org-row-${o.organization_id}`} className="border-b border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{o.organization_id}</td>
                <td className="px-3 py-2">{o.nombre}</td>
                <td className="px-3 py-2"><Badge variant={o.tipo === "celebra" ? "success" : "info"}>{o.tipo}</Badge></td>
                <td className="px-3 py-2">
                  {o._archivado ? <Badge variant="danger">archivada</Badge> : <Badge variant="success">activa</Badge>}
                  {o.protegida && <Badge variant="warning"> protegida</Badge>}
                </td>
                <td className="px-3 py-2 text-right">{o.conteos?.users || 0}</td>
                <td className="px-3 py-2 text-right">{o.conteos?.eventos || 0}</td>
                <td className="px-3 py-2 text-right">{o.conteos?.clientes || 0}</td>
                <td className="px-3 py-2 text-right">{o.conteos?.pagos || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">Unidades de Negocio ({unidades.length})</h2>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-3 py-2">ID</th><th className="text-left px-3 py-2">Nombre</th>
            <th className="text-left px-3 py-2">Color</th><th className="text-right px-3 py-2">Eventos</th>
            <th className="text-right px-3 py-2">Catálogo</th>
          </tr></thead>
          <tbody>
            {unidades.map(u => (
              <tr key={u.unit_id} data-testid={`unit-row-${u.unit_id}`} className="border-b border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{u.unit_id}</td>
                <td className="px-3 py-2">{u.nombre}</td>
                <td className="px-3 py-2"><span className="inline-block w-4 h-4 rounded" style={{ background: u.color }} /> <code className="text-xs">{u.color}</code></td>
                <td className="px-3 py-2 text-right">{u.conteos?.eventos || 0}</td>
                <td className="px-3 py-2 text-right">{u.conteos?.items_catalogo || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">Roles del sistema ({roles.length})</h2>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-3 py-2">ID</th><th className="text-left px-3 py-2">Nombre</th>
            <th className="text-left px-3 py-2">Único</th><th className="text-left px-3 py-2">Asignable</th>
            <th className="text-right px-3 py-2">Users</th>
          </tr></thead>
          <tbody>
            {roles.map(r => (
              <tr key={r.role_id} data-testid={`role-row-${r.role_id}`} className="border-b border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{r.role_id}</td>
                <td className="px-3 py-2">{r.nombre}</td>
                <td className="px-3 py-2">{r.unico ? "✓" : ""}</td>
                <td className="px-3 py-2">{r.asignable ? "✓" : "🔒"}</td>
                <td className="px-3 py-2 text-right">{r.conteos?.users_asignados || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function TabUsuarios({ usuarios, onDecidir }) {
  return (
    <div data-testid="tab-content-usuarios">
      <p className="text-sm text-slate-500 mb-3">
        {usuarios.length} usuarios pendientes. La sugerencia es solo una ayuda; NO se aplica sin tu aprobación.
      </p>
      <div className="space-y-3">
        {usuarios.map(u => (
          <UsuarioCard key={u.user_id} u={u} onDecidir={onDecidir} />
        ))}
      </div>
    </div>
  );
}

function UsuarioCard({ u, onDecidir }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`user-card-${u.user_id}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-semibold text-slate-900">{u.email}</div>
          <div className="text-xs text-slate-500 font-mono">{u.user_id}</div>
        </div>
        {u.archivado && <Badge variant="danger">archivado</Badge>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-3">
        <div>
          <div className="text-xs uppercase text-slate-500 mb-1">Estado actual</div>
          <ul className="space-y-1 text-slate-700">
            <li>Organization: <code className="text-xs">{u.estado_actual.organization_id_actual || "—"}</code></li>
            <li>Role: <code className="text-xs">{u.estado_actual.role_id_actual || "—"}</code></li>
            <li>Legacy: role={u.estado_actual.role_legacy}, rol={u.estado_actual.rol_legacy}, wsr={u.estado_actual.workspace_rol || "—"}</li>
            <li>Empresa legacy: <code className="text-xs">{u.estado_actual.empresa_id_legacy || "—"}</code></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase text-slate-500 mb-1">Sugerencia (no aplicada)</div>
          <ul className="space-y-1 text-slate-700">
            <li>Organization sugerida: <code className="text-xs">{u.sugerencia_organizacion.organization_id || "—"}</code></li>
            <li className="text-xs text-slate-500 italic">{u.sugerencia_organizacion.evidencia}</li>
            <li>Role sugerido: <code className="text-xs">{u.sugerencia_role.role_id || "—"}</code></li>
            <li className="text-xs text-slate-500 italic">{u.sugerencia_role.evidencia}</li>
          </ul>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-t pt-3 border-slate-100">
        {u.sugerencia_organizacion.organization_id && u.sugerencia_organizacion.organization_id !== u.estado_actual.organization_id_actual && (
          <button
            data-testid={`btn-aprobar-org-${u.user_id}`}
            className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
            onClick={() => {
              const motivo = window.prompt("Motivo (opcional):", "Aprobado por Propietario Global");
              if (motivo === null) return;
              onDecidir({
                entidad: "user", entidad_id: u.user_id, campo: "organization_id",
                accion: "aprobar", valor_sugerido: u.sugerencia_organizacion.organization_id, motivo,
              });
            }}
          >Aplicar organización sugerida</button>
        )}
        {u.sugerencia_role.role_id && (
          <button
            data-testid={`btn-aprobar-role-${u.user_id}`}
            className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
            onClick={() => {
              const motivo = window.prompt("Motivo (opcional):", "Aprobado por Propietario Global");
              if (motivo === null) return;
              onDecidir({
                entidad: "user", entidad_id: u.user_id, campo: "role_id",
                accion: "aprobar", valor_sugerido: u.sugerencia_role.role_id, motivo,
              });
            }}
          >Aplicar rol sugerido</button>
        )}
        <button
          data-testid={`btn-rechazar-${u.user_id}`}
          className="px-3 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
          onClick={() => {
            const motivo = window.prompt("Motivo del rechazo:");
            if (!motivo) return;
            onDecidir({
              entidad: "user", entidad_id: u.user_id, campo: "role_id",
              accion: "rechazar", motivo,
            });
          }}
        >Rechazar sugerencias</button>
        <button
          data-testid={`btn-posponer-${u.user_id}`}
          className="px-3 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
          onClick={() => {
            const motivo = window.prompt("Motivo para posponer:");
            if (motivo === null) return;
            onDecidir({
              entidad: "user", entidad_id: u.user_id, campo: "role_id",
              accion: "posponer", motivo,
            });
          }}
        >Posponer</button>
      </div>
    </div>
  );
}

function TabEventos({ eventos, onDecidir }) {
  return (
    <div data-testid="tab-content-eventos">
      <p className="text-sm text-slate-500 mb-3">{eventos.length} eventos con clasificación pendiente.</p>
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-700"><tr>
          <th className="text-left px-3 py-2">Evento</th>
          <th className="text-left px-3 py-2">Fecha</th>
          <th className="text-left px-3 py-2">Estatus</th>
          <th className="text-left px-3 py-2">Organización</th>
          <th className="text-left px-3 py-2">Unit IDs</th>
          <th className="text-left px-3 py-2">Trazabilidad</th>
        </tr></thead>
        <tbody>
          {eventos.slice(0, 100).map(e => (
            <tr key={e.evento_id} data-testid={`evento-row-${e.evento_id}`} className="border-b border-slate-100">
              <td className="px-3 py-2">
                <div className="font-medium">{e.nombre || "(sin nombre)"}</div>
                <div className="text-xs text-slate-400 font-mono">{e.evento_id}</div>
              </td>
              <td className="px-3 py-2">{e.fecha || "—"}</td>
              <td className="px-3 py-2"><Badge>{e.estatus || "—"}</Badge></td>
              <td className="px-3 py-2 font-mono text-xs">{e.organization_id_actual || "—"}</td>
              <td className="px-3 py-2 text-xs">{e.unit_ids_actual?.join(", ") || "[]"}</td>
              <td className="px-3 py-2 text-xs text-slate-500">
                <div>{e.trazabilidad.organization_id_source}</div>
                <div>{e.trazabilidad.unit_ids_source}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {eventos.length > 100 && (
        <p className="text-xs text-slate-500 mt-3">Mostrando 100 de {eventos.length} — filtro por lote en la siguiente iteración.</p>
      )}
    </div>
  );
}

function TabClientes({ clientes, onDecidir, organizaciones }) {
  return (
    <div data-testid="tab-content-clientes">
      <p className="text-sm text-slate-500 mb-3">{clientes.length} clientes sin organización asignada.</p>
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-700"><tr>
          <th className="text-left px-3 py-2">Cliente</th>
          <th className="text-left px-3 py-2">Teléfono</th>
          <th className="text-left px-3 py-2">Correo</th>
          <th className="text-left px-3 py-2">Usuario ID</th>
          <th className="text-left px-3 py-2">Acciones</th>
        </tr></thead>
        <tbody>
          {clientes.map(c => (
            <tr key={c.cliente_id} data-testid={`cliente-row-${c.cliente_id}`} className="border-b border-slate-100">
              <td className="px-3 py-2">{c.nombre || "—"}</td>
              <td className="px-3 py-2">{c.telefono || "—"}</td>
              <td className="px-3 py-2">{c.correo || "—"}</td>
              <td className="px-3 py-2 font-mono text-xs">{c.usuario_id}</td>
              <td className="px-3 py-2">
                <select
                  data-testid={`select-org-${c.cliente_id}`}
                  className="text-xs border rounded px-1 py-0.5"
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const motivo = window.prompt("Motivo:", "Asignado por Propietario Global");
                    if (motivo === null) return;
                    onDecidir({
                      entidad: "cliente", entidad_id: c.cliente_id, campo: "organization_id",
                      accion: "aprobar", valor_sugerido: e.target.value, motivo,
                    });
                  }}
                >
                  <option value="">— Elegir organización —</option>
                  {organizaciones.map(o => (
                    <option key={o.organization_id} value={o.organization_id}>{o.nombre}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabAmbiguas({ ambiguas }) {
  return (
    <div className="space-y-6" data-testid="tab-content-ambiguas">
      <section>
        <h2 className="text-lg font-semibold mb-2">Clientes con mismo teléfono ({ambiguas.duplicados_por_telefono?.length || 0})</h2>
        {(ambiguas.duplicados_por_telefono || []).length === 0 && <p className="text-slate-500 text-sm">Sin duplicados detectados.</p>}
        {(ambiguas.duplicados_por_telefono || []).map((g, i) => (
          <div key={i} data-testid={`amb-tel-${i}`} className="border border-amber-200 bg-amber-50 rounded-lg p-3 mb-2">
            <div className="text-sm font-medium">📞 {g.telefono} — {g.cantidad} clientes</div>
            <ul className="text-xs text-slate-600 mt-1">
              {g.clientes.map((c, j) => (
                <li key={j}>· <code>{c.cliente_id}</code> — {c.nombre} (org: {c.organization_id || "—"})</li>
              ))}
            </ul>
          </div>
        ))}
      </section>
      <section>
        <h2 className="text-lg font-semibold mb-2">Clientes con mismo correo ({ambiguas.duplicados_por_correo?.length || 0})</h2>
        {(ambiguas.duplicados_por_correo || []).length === 0 && <p className="text-slate-500 text-sm">Sin duplicados detectados.</p>}
        {(ambiguas.duplicados_por_correo || []).map((g, i) => (
          <div key={i} data-testid={`amb-mail-${i}`} className="border border-amber-200 bg-amber-50 rounded-lg p-3 mb-2">
            <div className="text-sm font-medium">✉ {g.correo} — {g.cantidad} clientes</div>
            <ul className="text-xs text-slate-600 mt-1">
              {g.clientes.map((c, j) => (
                <li key={j}>· <code>{c.cliente_id}</code> — {c.nombre} (org: {c.organization_id || "—"})</li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </div>
  );
}

function TabHistorial({ historial }) {
  return (
    <div data-testid="tab-content-historial">
      <p className="text-sm text-slate-500 mb-3">{historial.length} decisiones registradas.</p>
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-700"><tr>
          <th className="text-left px-3 py-2">Fecha</th>
          <th className="text-left px-3 py-2">Usuario</th>
          <th className="text-left px-3 py-2">Acción</th>
          <th className="text-left px-3 py-2">Entidad</th>
          <th className="text-left px-3 py-2">Campo</th>
          <th className="text-left px-3 py-2">Cambio</th>
          <th className="text-left px-3 py-2">Motivo</th>
        </tr></thead>
        <tbody>
          {historial.map((h, i) => (
            <tr key={i} data-testid={`historial-row-${i}`} className="border-b border-slate-100">
              <td className="px-3 py-2 text-xs">{h.created_at}</td>
              <td className="px-3 py-2 text-xs">{h.user_email}</td>
              <td className="px-3 py-2"><Badge variant={h.accion === "aprobar" ? "success" : "warning"}>{h.accion}</Badge></td>
              <td className="px-3 py-2 text-xs">{h.entidad}</td>
              <td className="px-3 py-2 text-xs font-mono">{h.campo}</td>
              <td className="px-3 py-2 text-xs">
                <div>De: <code>{JSON.stringify(h.valor_previo)}</code></div>
                <div>A: <code>{JSON.stringify(h.valor_nuevo)}</code></div>
              </td>
              <td className="px-3 py-2 text-xs italic">{h.motivo || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
